import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { EMPTY } from "rxjs";
import { filter, switchMap, take, takeUntil, tap } from "rxjs/operators";

import { AUTH_ACTION_TYPE } from "../../../auth/auth.action";
import { DialogStatusSelector } from "../../../dialog-status/dialog-status.selector";
import { CORE_ROUTE } from "../../../route";
import { CoreState } from "../../../state";
import { RESPONSIBLE_GAMING_ROUTE } from "../../responsible-gaming.const";
import { DepositLimitConfigService } from "../deposit-limit.config";

import { DepositLimitRequiredService } from "./deposit-limit-required.service";

@Injectable()
export class DepositLimitRequiredEffect {
	/**
	 * This effect covers situation when user accesses page requiring Deposit Limit in logged out mode
	 * and then logins without changing a route (e.g. in desktop thru login popup).
	 * DepositLimitRequiredGuard won't help in this scenario.
	 */
	checkOnSessionStart$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(AUTH_ACTION_TYPE.sessionStart),
				switchMap(() =>
					this.depositLimitConfig.getIsDepositLimitRequired()
						? this.store.select(RouteSelector.getCurrent).pipe(
								ignoreNil(),
								take(1),
								switchMap((current) => {
									if (!current.data?.depositLimitRequired) {
										return EMPTY;
									}

									let depositRequiredDialogOpened = false;
									return this.store.pipe(
										select(this.dialogSelector.hasOpenDialogs()),
										// check that we are still on the same page after dialogs close
										takeUntil(
											this.store.select(RouteSelector.getCurrent).pipe(
												ignoreNil(),
												filter((newCurrent) => newCurrent.name !== current.name)
											)
										),
										filter((openedDialog) => !openedDialog),
										switchMap(() => this.depositLimitRequiredService.shouldOpenDepositLimit$()),
										filter((shouldOpen) => shouldOpen),
										tap(() => {
											// user just closed the dialog without filling in
											if (depositRequiredDialogOpened) {
												this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home));
												return;
											}

											depositRequiredDialogOpened = true;
											this.store.dispatch(
												RouteAction.navigate(RESPONSIBLE_GAMING_ROUTE.depositLimitDialog)
											);
										})
									);
								})
							)
						: EMPTY
				)
			),
		{ dispatch: false }
	);

	constructor(
		private actions$: Actions,
		private depositLimitConfig: DepositLimitConfigService,
		private depositLimitRequiredService: DepositLimitRequiredService,
		private dialogSelector: DialogStatusSelector,
		private store: Store<CoreState>
	) {}
}
